import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Layers, Plus, X, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import {
  fetchGroups,
  fetchPermissionsMap,
  userGroupsFromMap,
  linkGroup,
  unlinkGroup,
  type User,
} from '@/lib/traccar-api';

interface EditUserGroupsDialogProps {
  user: User;
  trigger?: React.ReactNode;
  singleGroup?: boolean;
}

export function EditUserGroupsDialog({ user, trigger, singleGroup }: EditUserGroupsDialogProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [addAllProgress, setAddAllProgress] = useState<{ done: number; total: number } | null>(null);

  const { data: allGroups = [], isLoading: loadingAll } = useQuery({
    queryKey: ['groups'],
    queryFn: fetchGroups,
    enabled: open,
  });

  const { data: permsMap } = useQuery({
    queryKey: ['permissions-map'],
    queryFn: fetchPermissionsMap,
    enabled: open,
  });

  const userGroups = permsMap ? userGroupsFromMap(user.id, allGroups, permsMap) : [];
  const loadingUser = !permsMap;

  const userGroupIds = new Set(userGroups.map(g => g.id));

  const linkMutation = useMutation({
    mutationFn: async (groupId: number) => {
      if (singleGroup && userGroups.length > 0) {
        await Promise.all(userGroups.map(g => unlinkGroup(user.id, g.id)));
      }
      return linkGroup(user.id, groupId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions-map'] });
      toast.success(t('users.groupLinked'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('users.groupLinkError'));
    },
  });

  const unlinkMutation = useMutation({
    mutationFn: (groupId: number) => unlinkGroup(user.id, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['permissions-map'] });
      toast.success(t('users.groupUnlinked'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('users.groupUnlinkError'));
    },
  });

  const availableGroups = allGroups.filter(
    g => !userGroupIds.has(g.id) && g.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleAddAll = async () => {
    const groups = [...availableGroups];
    setAddAllProgress({ done: 0, total: groups.length });
    let succeeded = 0;
    let failed = 0;
    for (const group of groups) {
      try {
        await linkGroup(user.id, group.id);
        succeeded++;
      } catch {
        failed++;
      }
      setAddAllProgress({ done: succeeded + failed, total: groups.length });
    }
    setAddAllProgress(null);
    queryClient.invalidateQueries({ queryKey: ['permissions-map'] });
    if (failed === 0) {
      toast.success(t('users.allGroupsLinked', { count: succeeded }));
    } else {
      toast.warning(t('users.allGroupsLinkedPartial', { succeeded, failed }));
    }
  };

  const isLoading = loadingAll || loadingUser;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <Layers className="h-3.5 w-3.5" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('users.editGroups')}</DialogTitle>
          <DialogDescription>
            {t('users.editGroupsDescription', { name: user.name })}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Associated groups */}
            <div>
              <h4 className="text-sm font-medium mb-2">{singleGroup ? t('users.associatedGroup') : t('users.associatedGroups')}</h4>
              {userGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">{t('users.noGroupsAssociated')}</p>
              ) : (
                <ScrollArea className="h-[120px]">
                  <div className="flex flex-wrap gap-2">
                    {userGroups.map(group => (
                      <Badge key={group.id} variant="secondary" className="gap-1 pr-1">
                        {group.name}
                        <button
                          onClick={() => unlinkMutation.mutate(group.id)}
                          disabled={unlinkMutation.isPending}
                          className="ml-1 rounded-full p-0.5 hover:bg-destructive/20 hover:text-destructive transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </div>

            {/* Available groups */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium">{t('users.availableGroups')}</h4>
                {!singleGroup && availableGroups.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1 text-xs"
                    onClick={handleAddAll}
                    disabled={!!addAllProgress || linkMutation.isPending}
                  >
                    {addAllProgress ? (
                      <>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        {addAllProgress.done}/{addAllProgress.total}
                      </>
                    ) : (
                      <>
                        <PlusCircle className="h-3.5 w-3.5" />
                        {t('users.addAll')}
                      </>
                    )}
                  </Button>
                )}
              </div>
              <Input
                placeholder={t('users.searchGroups')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="mb-2"
              />
              <ScrollArea className="h-[160px] border rounded-md">
                {availableGroups.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-3">
                    {search ? t('users.noGroupsMatch') : t('users.allGroupsAssigned')}
                  </p>
                ) : (
                  <div className="p-2 space-y-1">
                    {availableGroups.map(group => (
                      <div
                        key={group.id}
                        className="flex items-center justify-between p-2 rounded-md hover:bg-muted/50"
                      >
                        <span className="text-sm">{group.name}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => linkMutation.mutate(group.id)}
                          disabled={linkMutation.isPending}
                          className="h-7 gap-1"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          {t('common.add')}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
